import React, { Component } from "react";
import SEO from "react-seo-component";
import { Badge, Button, Card, CardBody, Col, Container, Row } from "reactstrap";

class Section extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFeature: null,
    };
  }

  scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    return (
      <React.Fragment>
        <SEO
          title="QikTruck Marketplace Builder Program - Empowering South Africa's E-commerce Founders"
          titleTemplate="QikTruck"
          titleSeparator="-"
          description="Join QikTruck's Marketplace Builder Program. Get logistics infrastructure, technical support, seed funding up to R100,000, and build the next generation of African marketplaces."
          image="https://media.qiktruck.co.za/marketplace-builder-program.png"
          pathname="https://qiktruck.co.za/marketplace-builder-program"
          twitterUsername="qiktrucksa"
        />

        {/* Hero Section */}
        <section id="home" className="marketplace-hero">
          <div className="hero-gradient-overlay">
            <Container>
              <Row className="align-items-center min-vh-100">
                <Col lg={6} md={12}>
                  <div className="hero-content text-white">
                    <Badge color="success" pill className="mb-3 px-3 py-2">
                      🚀 Now Accepting Applications
                    </Badge>
                    <h1 className="hero-title mb-4 text-white">
                      <span className="highlight-text">QikTruck</span><br />
                      Marketplace Builder Program
                    </h1>
                    <p className="hero-subtitle mb-4">
                      Empowering South Africa's Next Generation of E-commerce Founders
                    </p>
                    <p className="hero-description mb-5">
                      Get the tools, logistics infrastructure, and funding you need to build 
                      and scale your own e-commerce marketplace. Join the future of African commerce.
                    </p>
                    <div className="hero-actions">
                      <Button 
                        color="success" 
                        size="lg" 
                        className="me-3 mb-3"
                        onClick={() => this.scrollToSection('how-to-join')}
                      >
                        Apply Now
                      </Button>
                      <Button 
                        color="outline-light" 
                        size="lg" 
                        className="mb-3"
                        style={{marginLeft: '10px'}}
                        onClick={() => this.scrollToSection('program')}
                      >
                        Learn More
                      </Button>
                    </div>
                    <div className="hero-stats mt-5">
                      <Row>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">R100K</div>
                          <div className="stat-label">Total Support per Participant</div>
                        </Col>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">5000+</div>
                          <div className="stat-label">Vehicles</div>
                        </Col>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">24/7</div>
                          <div className="stat-label">Support</div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                <Col lg={6} md={12}>
                  <div className="hero-image-container">
                    <img 
                      src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                      alt="African entrepreneurs building marketplaces"
                      className="hero-image"
                    />
                    <div className="floating-card">
                      <div className="card-content">
                        <h5>Join 50+ Builders</h5>
                        <p>Building the future of African commerce</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </section>

        {/* Target Audience Section */}
        <section id="program" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">Who Is This For?</h2>
                <p className="section-subtitle">
                  We're looking for ambitious entrepreneurs ready to transform African commerce
                </p>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={6} className="mb-4">
                <Card className="target-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-rocket text-success" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Aspiring Founders</h4>
                    <p>Building e-commerce marketplaces from the ground up with innovative ideas</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="target-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-chart-line text-success" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Existing Small Marketplaces</h4>
                    <p>Looking to scale operations and reach new markets across Africa</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="target-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-map-marker-alt text-success" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Niche Platform Builders</h4>
                    <p>Targeting local, township, or rural commerce with specialized solutions</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="target-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-store text-success" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Established Retailers</h4>
                    <p>Traditional businesses ready to digitize and expand their reach online</p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* What We Offer Section */}
        <section id="benefits" className="py-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">What We Offer</h2>
                <p className="section-subtitle">
                  Everything you need to build, launch, and scale your marketplace
                </p>
              </Col>
            </Row>

            {/* Feature 1: Logistics */}
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="mb-4">
                <div className="feature-content">
                  <Badge color="success" className="mb-3">1. Logistics-as-a-Service</Badge>
                  <h3>Nationwide Fleet Access</h3>
                  <p className="feature-description">
                    Access to QikTruck's nationwide fleet of over 5,000 vehicles – from bakkies 
                    and motorbikes to 16-ton trucks. Builders can plug into our API or dashboard 
                    to handle deliveries, collections, and returns seamlessly.
                  </p>
                  <ul className="feature-list">
                    <li>🚛 5000+ vehicles nationwide</li>
                    <li>📱 Easy API integration</li>
                    <li>📦 End-to-end delivery management</li>
                    <li>🔄 Returns handling</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12}>
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="QikTruck logistics fleet"
                  className="feature-image"
                />
              </Col>
            </Row>

            {/* Feature 2: Infrastructure */}
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="order-lg-2 mb-4 p-4">
                <div className="feature-content">
                  <Badge color="primary" className="mb-3">2. E-commerce Infrastructure</Badge>
                  <h3>Ready-to-Use Platform</h3>
                  <p className="feature-description">
                    No need to start from scratch. Get access to proven e-commerce infrastructure 
                    that powers successful marketplaces.
                  </p>
                  <ul className="feature-list">
                    <li>📊 Product & inventory management systems</li>
                    <li>💳 Payment gateway integration</li>
                    <li>🏪 Multi-vendor marketplace templates</li>
                    <li>💰 Delivery pricing calculator API</li>
                    <li>📍 Order tracking and proof of delivery</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12} className="order-lg-1">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="E-commerce platform dashboard"
                  className="feature-image"
                />
              </Col>
            </Row>

            {/* Feature 3: Technical Support */}
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="mb-4">
                <div className="feature-content">
                  <Badge color="warning" className="mb-3">3. Technical Support</Badge>
                  <h3>Expert Mentorship</h3>
                  <p className="feature-description">
                    Builders receive mentorship and access to developer support for custom 
                    marketplace flows and technical challenges.
                  </p>
                  <ul className="feature-list">
                    <li>👨‍💻 Dedicated developer support</li>
                    <li>🎯 Custom marketplace flow development</li>
                    <li>📚 Technical documentation and guides</li>
                    <li>🤝 One-on-one mentorship sessions</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12}>
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Technical support and mentorship"
                  className="feature-image"
                />
              </Col>
            </Row>

            {/* Feature 4: Funding */}
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="order-lg-2 mb-4  p-4">
                <div className="feature-content">
                  <Badge color="danger" className="mb-3">4. Seed Funding & Incentives</Badge>
                  <h3>Up to R100,000 Support</h3>
                  <div className="funding-cards">
                    <Card className="funding-card mb-3">
                      <CardBody>
                        <h5 className="text-success">R50,000</h5>
                        <p>Starter logistics credits</p>
                      </CardBody>
                    </Card>
                    <Card className="funding-card mb-3">
                      <CardBody>
                        <h5 className="text-primary">R50,000</h5>
                        <p>Marketing support</p>
                      </CardBody>
                    </Card>
                  </div>
                  <p>We also connect promising builders with QikTruck's network of suppliers and buyers.</p>
                </div>
              </Col>
              <Col lg={6} md={12} className="order-lg-1">
                <img 
                  src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Seed funding and investment"
                  className="feature-image"
                />
              </Col>
            </Row>

            {/* Feature 5: Go-to-Market */}
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="mb-4">
                <div className="feature-content">
                  <Badge color="info" className="mb-3">5. Go-to-Market Acceleration</Badge>
                  <h3>Launch Support</h3>
                  <ul className="feature-list">
                    <li>🎯 Co-branded launch campaigns</li>
                    <li>🚗 Shared driver network for pilot rollouts</li>
                    <li>🏪 Spaza network integration where relevant</li>
                    <li>📱 Social media visibility and community engagement tools</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12}>
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Go-to-market acceleration"
                  className="feature-image"
                />
              </Col>
            </Row>
          </Container>
        </section>

        {/* Success Stories Section */}
        <section className="py-5 bg-light" style={{display: 'none'}}>
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">Success Stories</h2>
                <p className="section-subtitle">
                  Hear from founders who are building the future of African commerce
                </p>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={6} className="mb-4">
                <Card className="testimonial-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center">
                    <img 
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                      alt="Thabo Mthembu"
                      className="testimonial-avatar mb-3"
                    />
                    <h5>Thabo Mthembu</h5>
                    <p className="text-muted">Township Fresh Markets</p>
                    <p className="testimonial-text">
                      "QikTruck's program gave us the logistics backbone we needed. 
                      We've scaled from one township to five cities in just 6 months."
                    </p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="testimonial-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center">
                    <img 
                      src="https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                      alt="Nomsa Dlamini"
                      className="testimonial-avatar mb-3"
                    />
                    <h5>Nomsa Dlamini</h5>
                    <p className="text-muted">Rural Connect</p>
                    <p className="testimonial-text">
                      "The technical support and mentorship were game-changers. 
                      Our platform now serves over 200 rural vendors."
                    </p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="testimonial-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center">
                    <img 
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80" 
                      alt="Sipho Ndlovu"
                      className="testimonial-avatar mb-3"
                    />
                    <h5>Sipho Ndlovu</h5>
                    <p className="text-muted">African Crafts Online</p>
                    <p className="testimonial-text">
                      "From idea to launch in 3 months. The funding and infrastructure 
                      support made all the difference."
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* How to Join Section */}
        <section id="how-to-join" className="py-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                <h2 className="section-title mb-4">How to Join</h2>
                <p className="section-subtitle mb-5">
                  Ready to build the next generation of African marketplaces? Here's how to get started.
                </p>
                
                <Card className="application-card border-0 shadow-lg mb-5">
                  <CardBody className="p-5">
                    <h3 className="mb-4">Application Requirements</h3>
                    <Row>
                      <Col md={6} className="text-left">
                        <h5>📝 What to Include:</h5>
                        <ul className="requirements-list">
                          <li>Your marketplace idea or existing platform</li>
                          <li>Target audience and market analysis</li>
                          <li>Current challenges or support required</li>
                          <li>Team background and experience</li>
                          <li>Revenue projections and business model</li>
                        </ul>
                      </Col>
                      <Col md={6} className="text-left">
                        <h5>🎯 What We Look For:</h5>
                        <ul className="requirements-list">
                          <li>Strong market understanding</li>
                          <li>Scalable business model</li>
                          <li>Commitment to African commerce</li>
                          <li>Technical aptitude or team</li>
                          <li>Community impact potential</li>
                        </ul>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <div className="apply-actions">
                  <Button 
                    color="outline-success" 
                    size="lg" 
                    className="mb-3"
                    onClick={() => window.open('mailto:build@qiktruck.co.za', '_blank')}
                  >
                    Email Application
                  </Button>
                </div>
                
                <p className="mt-4 text-muted">
                  Apply via email <strong>support@qiktruck.co.za</strong>
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="cta-section py-5">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={8}>
                <h2 className="text-white mb-4">
                  Ready to Build the Future of African Commerce?
                </h2>
                <p className="text-white mb-4">
                  Together, let's build the next generation of African marketplaces – faster, smarter, and locally rooted.
                </p>
                <Button 
                  color="light" 
                  size="lg"
                  onClick={() => this.scrollToSection('how-to-join')}
                >
                  Start Your Application
                </Button>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                <h3>Questions About the Program?</h3>
                <p className="mb-4">
                  Our team is here to help you understand how the Marketplace Builder Program 
                  can accelerate your e-commerce journey.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        <style jsx>{`
          .marketplace-hero {
            background: linear-gradient(135deg, #1e3a8a 0%, #10b981 100%);
            position: relative;
            overflow: hidden;
          }
          
          .hero-gradient-overlay {
            background: rgba(0,0,0,0.1);
            min-height: 100vh;
            display: flex;
            align-items: center;
          }
          
          .hero-title {
            font-size: 3.5rem;
            font-weight: 800;
            line-height: 1.1;
          }
          
          .highlight-text {
            color: #10b981;
          }
          
          .hero-subtitle {
            font-size: 1.5rem;
            font-weight: 600;
            opacity: 0.9;
          }
          
          .hero-description {
            font-size: 1.1rem;
            opacity: 0.8;
            line-height: 1.6;
          }
          
          .hero-image {
            width: 100%;
            height: 400px;
            object-fit: cover;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          }
          
          .floating-card {
            position: absolute;
            bottom: 20px;
            left: 20px;
            background: white;
            padding: 20px;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.2);
            max-width: 200px;
          }
          
          .stat-number {
            font-size: 2rem;
            font-weight: 700;
            color: #10b981;
          }
          
          .stat-label {
            font-size: 0.9rem;
            opacity: 0.8;
          }
          
          .section-title {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 1rem;
          }
          
          .section-subtitle {
            font-size: 1.2rem;
            color: #6b7280;
            line-height: 1.6;
          }
          
          .target-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          
          .target-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
          }
          
          .feature-image {
            width: 100%;
            height: 300px;
            object-fit: cover;
            border-radius: 15px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          }
          
          .feature-list {
            list-style: none;
            padding-left: 0;
          }
          
          .feature-list li {
            padding: 8px 0;
            font-size: 1.1rem;
          }
          
          .funding-card {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border: none;
          }
          
          .testimonial-avatar {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
          }
          
          .testimonial-card {
            transition: transform 0.3s ease;
          }
          
          .testimonial-card:hover {
            transform: translateY(-5px);
          }
          
          .testimonial-text {
            font-style: italic;
            color: #6b7280;
          }
          
          .application-card {
            background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
          }
          
          .requirements-list {
            list-style: none;
            padding-left: 0;
          }
          
          .requirements-list li {
            padding: 5px 0;
            position: relative;
            padding-left: 20px;
          }
          
          .requirements-list li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #10b981;
            font-weight: bold;
          }
          
          .cta-section {
            background: linear-gradient(135deg, #1e3a8a 0%, #10b981 100%);
          }
          
          @media (max-width: 768px) {
            .hero-title {
              font-size: 2.5rem;
            }
            
            .section-title {
              font-size: 2rem;
            }
            
            .hero-image {
              height: 250px;
            }
            
            .feature-image {
              height: 200px;
            }
          }
        `}</style>
      </React.Fragment>
    );
  }
}

export default Section; 